/**
 * Inicialização do Firebase no painel.
 *
 * As chaves vêm de `import.meta.env` (ver `.env.example`). Se elas não estiverem presentes, o
 * painel **não** quebra: `hasFirebaseConfig` fica falso e a camada de dados escolhe o modo demo.
 * Isso mantém o painel utilizável antes de o projeto estar provisionado.
 *
 * A região das callables tem que casar com a das Functions (`REGION` em
 * `apps/backend/src/index.ts`). Divergir aqui dá o clássico "function not found" que parece bug de
 * permissão.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';

export const FUNCTIONS_REGION = 'southamerica-east1';

/**
 * Banco Firestore nomeado. O `(default)` deste projeto já pertence a outra aplicação (em `nam5`),
 * então o webchat vive num banco próprio — ver `apps/backend/src/database.ts` para o raciocínio.
 * Este valor tem que casar com o `database` do array `firestore` em `firebase.json`.
 */
export const FIRESTORE_DATABASE_ID =
  (import.meta.env.VITE_FIRESTORE_DATABASE_ID as string | undefined) ?? 'merlin';

const env = import.meta.env;

const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY as string | undefined,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined,
  projectId: env.VITE_FIREBASE_PROJECT_ID as string | undefined,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET as string | undefined,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID as string | undefined,
  appId: env.VITE_FIREBASE_APP_ID as string | undefined,
};

export const hasFirebaseConfig = Boolean(
  firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId,
);

export const useEmulators = env.VITE_USE_EMULATORS === '1';

/** Base das rotas públicas, usada pelo snippet de instalação e pelo preview. */
export const apiBase =
  (env.VITE_API_BASE as string | undefined) ??
  (firebaseConfig.projectId ? `https://${FUNCTIONS_REGION}-${firebaseConfig.projectId}.cloudfunctions.net` : '');

let app: FirebaseApp | null = null;
let authInstance: Auth | null = null;
let dbInstance: Firestore | null = null;
let functionsInstance: Functions | null = null;
let storageInstance: FirebaseStorage | null = null;

function ensureApp(): FirebaseApp {
  if (!hasFirebaseConfig) {
    throw new Error(
      'Firebase não configurado. Preencha apps/admin/.env.local (veja .env.example) ou rode com VITE_DEMO=1.',
    );
  }
  if (!app) {
    app = initializeApp({
      apiKey: firebaseConfig.apiKey!,
      authDomain: firebaseConfig.authDomain!,
      projectId: firebaseConfig.projectId!,
      storageBucket: firebaseConfig.storageBucket!,
      messagingSenderId: firebaseConfig.messagingSenderId!,
      appId: firebaseConfig.appId!,
    });
  }
  return app;
}

export function getAuthClient(): Auth {
  if (!authInstance) {
    authInstance = getAuth(ensureApp());
    if (useEmulators) connectAuthEmulator(authInstance, 'http://127.0.0.1:9099', { disableWarnings: true });
  }
  return authInstance;
}

export function getDb(): Firestore {
  if (!dbInstance) {
    dbInstance = getFirestore(ensureApp(), FIRESTORE_DATABASE_ID);
    if (useEmulators) connectFirestoreEmulator(dbInstance, '127.0.0.1', 8080);
  }
  return dbInstance;
}

export function getFunctionsClient(): Functions {
  if (!functionsInstance) {
    functionsInstance = getFunctions(ensureApp(), FUNCTIONS_REGION);
    if (useEmulators) connectFunctionsEmulator(functionsInstance, '127.0.0.1', 5001);
  }
  return functionsInstance;
}

export function getStorageClient(): FirebaseStorage {
  if (!storageInstance) {
    storageInstance = getStorage(ensureApp());
    if (useEmulators) connectStorageEmulator(storageInstance, '127.0.0.1', 9199);
  }
  return storageInstance;
}

export const projectId = firebaseConfig.projectId ?? null;
